'use client';

import { Box, Flex, Link, Typography } from '@coolblue-development/freddy';
import clickReviewsInProductInformationTrigger from '@coolblue-development/ga4-events/triggers/ProductInformation/clickReviewsInProductInformationTrigger';
import { ArrowRight } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { ProsAndCons } from '@coolblue-development/next-patterns/client';
import type { Product } from '@features/product/typings';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { useTranslation } from '@i18n/useTranslation';
import { scrollToTarget } from '@utils/utils';

import { useProductInformation } from './ProductInformationContext';

const ProsAndConsSection = ({
  product,
  reviewsSectionId,
  hideLink = false
}: {
  product: Product;
  reviewsSectionId?: string;
  hideLink?: boolean;
}) => {
  const { t } = useTranslation('webshop.productPage.productInformation');
  const { openModal } = useProductInformation();
  const { isMobile } = useBreakpoints();
  const { pros, cons } = product.prosAndCons;
  const { trackEvent } = useAnalytics();

  const params = {
    item_id: product.id
  };

  if (pros.length === 0 && cons.length === 0) {
    return null;
  }

  return (
    <Box component='section'>
      <Flex
        alignItems={{ xs: 'flex-start', md: 'baseline' }}
        direction={{ xs: 'column', md: 'row' }}
        mt={{ xs: 400, md: 0 }}
        mb={200}
      >
        <Typography variant='h3'>{t('prosAndConsTitle')}</Typography>
        <Typography variant='caption' display='inline' ml={{ md: 200 }}>
          {t('accordingToOurSpecialist', {
            singularName: product.productType.singularName
          })}
        </Typography>
      </Flex>
      {isMobile && !hideLink ? (
        <Box>
          <ProsAndCons pros={pros.slice(0, 2)} cons={cons.slice(0, 1)} iconSize='medium' />
          <Link component='button' startIcon={<ArrowRight />} onClick={openModal} mt={200}>
            {t('allProsAndCons')}
          </Link>
        </Box>
      ) : (
        <ProsAndCons pros={pros.slice(0, 3)} cons={cons.slice(0, 2)} iconSize='medium' />
      )}
      {reviewsSectionId && (
        <Link
          href={`#${reviewsSectionId}`}
          startIcon={<ArrowRight />}
          mt={200}
          onClick={event => {
            scrollToTarget(reviewsSectionId)(event);
            trackEvent(clickReviewsInProductInformationTrigger(params));
          }}
        >
          {t('readExpertReview')}
        </Link>
      )}
    </Box>
  );
};

export default ProsAndConsSection;
